import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/table-cell.default.js';
import * as wireframeSkin from './skins/table-cell.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunTableCell extends RunElement {
  #skin = null;
  #bordered = false;
  #rendering = false;

  static get observedAttributes() {
    return ['skin', 'bordered'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'skin':
        this.#skin = newValue;
        this.requestUpdate();
        break;
      case 'bordered':
        this.bordered = newValue !== null;
        break;
    }
  }

  get skin() {
    return this.#skin;
  }

  set skin(value) {
    const coerced = value == null ? null : String(value);
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced === null) {
      this.removeAttribute('skin');
    } else {
      this.setAttribute('skin', coerced);
    }
    this.requestUpdate();
  }

  get bordered() {
    return this.#bordered;
  }

  set bordered(value) {
    const coerced = Boolean(value);
    if (this.#bordered === coerced) return;
    this.#bordered = coerced;
    this.toggleAttribute('bordered', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    if (this.#skin) return this.#skin;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    // role="cell" lives on [part="base"] inside the shadow DOM, not on the host.
  }
}

if (!customElements.get('run-table-cell')) {
  customElements.define('run-table-cell', RunTableCell);
}

export { RunTableCell };
export default RunTableCell;